'use client'

import React, { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Separator } from '../ui/separator'
import { Progress } from '../ui/progress'
import { 
  Image, 
  Wand2,
  Copy,
  CheckCircle,
  Save,
  Download,
  Upload,
  Trash2,
  Eye,
  EyeOff,
  Ruler,
  Palette,
  Grid3X3,
  AlertTriangle,
  Plus,
  X
} from 'lucide-react'
import { Brand } from '../../utils/mockData'

interface LogoGuidelinesProps {
  brand: Brand
}

interface LogoVariation {
  id: string
  name: string
  description: string
  usage: string
  file?: File | null
  preview?: string
}

interface LogoGuidelines {
  primaryLogo: {
    description: string
    usage: string
    file?: File | null
    preview?: string
  }
  variations: LogoVariation[]
  clearSpace: {
    minimum: string
    calculation: string
    description: string
  }
  minimumSizes: {
    print: string
    digital: string
    description: string
  }
  colorUsage: {
    primary: string[]
    onDark: string[]
    onLight: string[]
    restrictions: string[]
  }
  dosList: string[]
  dontsList: string[]
  usageExamples: {
    businessCard: boolean
    website: boolean
    signage: boolean
    merchandise: boolean
    socialMedia: boolean
    email: boolean
  }
}

const DEFAULT_LOGO_VARIATIONS = [
  {
    id: 'horizontal',
    name: 'Horizontal Logo',
    description: 'Full logo with text positioned horizontally',
    usage: 'Headers, business cards, letterheads'
  },
  {
    id: 'stacked',
    name: 'Stacked Logo',
    description: 'Logo with text stacked vertically',
    usage: 'Square spaces, social media profiles'
  },
  {
    id: 'icon',
    name: 'Icon Only',
    description: 'Logo mark without text',
    usage: 'Favicons, app icons, small spaces'
  },
  {
    id: 'monochrome',
    name: 'Monochrome Version',
    description: 'Single color version for special applications',
    usage: 'Fax, embossing, single-color printing'
  }
]

const USAGE_SCENARIOS = [
  { key: 'businessCard', label: 'Business Cards', icon: '💳' },
  { key: 'website', label: 'Website Header', icon: '🌐' },
  { key: 'signage', label: 'Signage', icon: '🪧' },
  { key: 'merchandise', label: 'Merchandise', icon: '👕' },
  { key: 'socialMedia', label: 'Social Media', icon: '📱' },
  { key: 'email', label: 'Email Signature', icon: '📧' }
]

export function LogoGuidelines({ brand }: LogoGuidelinesProps) {
  const [guidelines, setGuidelines] = useState<LogoGuidelines>({
    primaryLogo: {
      description: '',
      usage: 'Primary logo for all brand communications',
      file: null,
      preview: undefined
    },
    variations: DEFAULT_LOGO_VARIATIONS.map(v => ({ ...v, file: null, preview: undefined })),
    clearSpace: {
      minimum: '1x logo height',
      calculation: 'Equal to the height of the logo mark',
      description: 'Minimum clear space around the logo to ensure visibility and impact'
    },
    minimumSizes: {
      print: '0.5 inches / 12.7mm',
      digital: '120px width',
      description: 'Minimum sizes to maintain legibility'
    },
    colorUsage: {
      primary: [],
      onDark: [],
      onLight: [],
      restrictions: []
    },
    dosList: [
      'Use the primary logo whenever possible',
      'Maintain minimum clear space requirements',
      'Use approved color variations only',
      'Ensure logo is legible at minimum sizes'
    ],
    dontsList: [
      'Don\'t stretch or distort the logo',
      'Don\'t use unapproved colors',
      'Don\'t place logo on busy backgrounds',
      'Don\'t recreate or modify the logo'
    ],
    usageExamples: {
      businessCard: true,
      website: true,
      signage: true,
      merchandise: false,
      socialMedia: true,
      email: true
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [newDo, setNewDo] = useState('')
  const [newDont, setNewDont] = useState('')
  const [showClearSpaceDemo, setShowClearSpaceDemo] = useState(false)

  useEffect(() => {
    loadGuidelinesData()
  }, [brand.id])

  const loadGuidelinesData = async () => {
    setIsLoading(true)
    try {
      const savedData = localStorage.getItem(`logoGuidelines_${brand.id}`)
      if (savedData) {
        setGuidelines(JSON.parse(savedData))
      }
    } catch (error) {
      console.error('Error loading logo guidelines:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const saveGuidelinesData = async () => {
    setIsSaving(true)
    try {
      localStorage.setItem(`logoGuidelines_${brand.id}`, JSON.stringify(guidelines))
      setLastSaved(new Date())
    } catch (error) {
      console.error('Error saving logo guidelines:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleFileUpload = (type: 'primary' | 'variation', id?: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const preview = URL.createObjectURL(file)
      
      if (type === 'primary') {
        setGuidelines(prev => ({
          ...prev,
          primaryLogo: { ...prev.primaryLogo, file, preview }
        }))
      } else if (type === 'variation' && id) {
        setGuidelines(prev => ({
          ...prev,
          variations: prev.variations.map(v => 
            v.id === id ? { ...v, file, preview } : v
          )
        }))
      }
    }
  }

  const updateVariation = (id: string, field: string, value: string) => {
    setGuidelines(prev => ({
      ...prev,
      variations: prev.variations.map(v => 
        v.id === id ? { ...v, [field]: value } : v
      )
    }))
  }

  const addColorRestriction = (color: string) => {
    if (!color.trim()) return
    setGuidelines(prev => ({
      ...prev,
      colorUsage: {
        ...prev.colorUsage,
        restrictions: [...prev.colorUsage.restrictions, color.trim()]
      }
    }))
  }

  const removeColorRestriction = (index: number) => {
    setGuidelines(prev => ({
      ...prev,
      colorUsage: {
        ...prev.colorUsage,
        restrictions: prev.colorUsage.restrictions.filter((_, i) => i !== index)
      }
    }))
  }

  const addToList = (listType: 'dosList' | 'dontsList', value: string, setValue: (val: string) => void) => {
    if (!value.trim()) return
    
    setGuidelines(prev => ({
      ...prev,
      [listType]: [...prev[listType], value.trim()]
    }))
    setValue('')
  }

  const removeFromList = (listType: 'dosList' | 'dontsList', index: number) => {
    setGuidelines(prev => ({
      ...prev,
      [listType]: prev[listType].filter((_, i) => i !== index)
    }))
  }

  const toggleUsageExample = (key: keyof LogoGuidelines['usageExamples']) => {
    setGuidelines(prev => ({
      ...prev,
      usageExamples: {
        ...prev.usageExamples,
        [key]: !prev.usageExamples[key]
      }
    }))
  }

  const generateAIGuidelines = async () => {
    setIsLoading(true)
    try {
      // Mock AI generation based on brand industry
      const industryGuidelines = {
        'Technology': {
          clearSpace: { minimum: '2x logo height', calculation: 'Double the height of the logo mark' },
          minimumSizes: { print: '0.75 inches / 19mm', digital: '150px width' },
          dosList: [
            'Use on clean, minimal backgrounds',
            'Ensure high contrast for digital displays',
            'Maintain pixel-perfect alignment',
            'Use vector formats when possible'
          ]
        },
        'Healthcare': {
          clearSpace: { minimum: '1.5x logo height', calculation: '1.5 times the height of the logo mark' },
          minimumSizes: { print: '0.6 inches / 15mm', digital: '140px width' },
          dosList: [
            'Ensure professional appearance',
            'Use on white or light backgrounds',
            'Maintain clean, medical aesthetic',
            'Follow accessibility guidelines'
          ]
        }
      }

      const industry = brand.metadata?.industry || 'Technology'
      const suggestion = industryGuidelines[industry as keyof typeof industryGuidelines] || industryGuidelines['Technology']

      setGuidelines(prev => ({
        ...prev,
        clearSpace: {
          ...prev.clearSpace,
          ...suggestion.clearSpace
        },
        minimumSizes: {
          ...prev.minimumSizes,
          ...suggestion.minimumSizes
        },
        dosList: suggestion.dosList
      }))
    } catch (error) {
      console.error('Error generating AI guidelines:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const exportGuidelines = () => {
    const guidelinesData = {
      brand: brand.name,
      guidelines,
      exported: new Date().toISOString()
    }
    
    const blob = new Blob([JSON.stringify(guidelinesData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${brand.name.toLowerCase().replace(/\s+/g, '-')}-logo-guidelines.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (isLoading && !guidelines.primaryLogo.description && guidelines.variations.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 bg-blue-600 rounded-full animate-pulse mx-auto mb-4"></div>
          <p className="text-slate-600">Loading logo guidelines...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Logo Guidelines</h2>
          <p className="text-slate-600">Define logo usage rules, spacing, and applications</p>
        </div>
        <div className="flex items-center space-x-3">
          {lastSaved && (
            <div className="flex items-center space-x-2 text-sm text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span>Saved {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
          <Button 
            variant="outline"
            onClick={exportGuidelines}
            size="sm"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button 
            onClick={saveGuidelinesData}
            disabled={isSaving}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Guidelines'}
          </Button>
        </div>
      </div>

      {/* AI Guidelines Generation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5" />
            AI Logo Guidelines
          </CardTitle>
          <CardDescription>
            Get AI-powered logo usage recommendations based on your industry
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-3">
            <Button 
              onClick={generateAIGuidelines}
              disabled={isLoading}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              {isLoading ? 'Generating...' : 'Generate AI Guidelines'}
            </Button>
            <span className="text-sm text-slate-600">
              Based on {brand.metadata?.industry || 'Technology'} industry standards
            </span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Primary Logo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              Primary Logo
            </CardTitle>
            <CardDescription>
              Main logo file and usage description
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
              {guidelines.primaryLogo.preview ? (
                <div className="space-y-4">
                  <img 
                    src={guidelines.primaryLogo.preview} 
                    alt="Primary Logo" 
                    className="max-h-32 mx-auto"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const input = document.createElement('input')
                      input.type = 'file'
                      input.accept = 'image/*'
                      input.onchange = handleFileUpload('primary')
                      input.click()
                    }}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Replace Logo
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Image className="w-12 h-12 text-slate-400 mx-auto" />
                  <div>
                    <p className="font-medium mb-2">Upload Primary Logo</p>
                    <p className="text-sm text-slate-500 mb-4">
                      SVG, PNG, or JPG format recommended
                    </p>
                    <Button
                      onClick={() => {
                        const input = document.createElement('input')
                        input.type = 'file'
                        input.accept = 'image/*'
                        input.onchange = handleFileUpload('primary')
                        input.click()
                      }}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Logo
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={guidelines.primaryLogo.description}
                onChange={(e) => setGuidelines(prev => ({
                  ...prev,
                  primaryLogo: { ...prev.primaryLogo, description: e.target.value }
                }))}
                placeholder="Describe the primary logo and its significance..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Primary Usage</Label>
              <Input
                value={guidelines.primaryLogo.usage}
                onChange={(e) => setGuidelines(prev => ({
                  ...prev,
                  primaryLogo: { ...prev.primaryLogo, usage: e.target.value }
                }))}
                placeholder="When and where to use this logo..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Logo Specifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ruler className="w-5 h-5" />
              Logo Specifications
            </CardTitle>
            <CardDescription>
              Size requirements and spacing guidelines
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Clear Space */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Clear Space Requirements</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowClearSpaceDemo(!showClearSpaceDemo)}
                >
                  {showClearSpaceDemo ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <Input
                value={guidelines.clearSpace.minimum}
                onChange={(e) => setGuidelines(prev => ({
                  ...prev,
                  clearSpace: { ...prev.clearSpace, minimum: e.target.value }
                }))}
                placeholder="e.g., 1x logo height"
              />
              <Input
                value={guidelines.clearSpace.calculation}
                onChange={(e) => setGuidelines(prev => ({
                  ...prev,
                  clearSpace: { ...prev.clearSpace, calculation: e.target.value }
                }))}
                placeholder="How to calculate clear space..."
              />
              {showClearSpaceDemo && (
                <div className="p-4 bg-slate-50 border rounded-lg">
                  <div className="relative">
                    <div className="w-16 h-16 bg-blue-600 rounded mx-auto mb-2 flex items-center justify-center text-white text-xs">
                      LOGO
                    </div>
                    <div className="absolute inset-0 border border-red-300 border-dashed"></div>
                    <div className="text-center text-xs text-red-600 mt-2">
                      Clear space visualization
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* Minimum Sizes */}
            <div className="space-y-3">
              <Label>Minimum Sizes</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Print</Label>
                  <Input
                    value={guidelines.minimumSizes.print}
                    onChange={(e) => setGuidelines(prev => ({
                      ...prev,
                      minimumSizes: { ...prev.minimumSizes, print: e.target.value }
                    }))}
                    placeholder="0.5 inches"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Digital</Label>
                  <Input
                    value={guidelines.minimumSizes.digital}
                    onChange={(e) => setGuidelines(prev => ({
                      ...prev,
                      minimumSizes: { ...prev.minimumSizes, digital: e.target.value }
                    }))}
                    placeholder="120px width"
                  />
                </div>
              </div>
              <Textarea
                value={guidelines.minimumSizes.description}
                onChange={(e) => setGuidelines(prev => ({
                  ...prev,
                  minimumSizes: { ...prev.minimumSizes, description: e.target.value }
                }))}
                placeholder="Additional size guidelines..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Logo Variations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Grid3X3 className="w-5 h-5" />
            Logo Variations
          </CardTitle>
          <CardDescription>
            Different versions of your logo for various applications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {guidelines.variations.map((variation) => (
              <div key={variation.id} className="space-y-4 p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">{variation.name}</h4>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const input = document.createElement('input')
                      input.type = 'file'
                      input.accept = 'image/*'
                      input.onchange = handleFileUpload('variation', variation.id)
                      input.click()
                    }}
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    Upload
                  </Button>
                </div>

                <div className="h-24 bg-slate-50 border border-dashed border-slate-200 rounded flex items-center justify-center">
                  {variation.preview ? (
                    <img 
                      src={variation.preview} 
                      alt={variation.name}
                      className="max-h-full max-w-full"
                    />
                  ) : (
                    <div className="text-center">
                      <Image className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                      <span className="text-xs text-slate-500">No image</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Input
                    value={variation.description}
                    onChange={(e) => updateVariation(variation.id, 'description', e.target.value)}
                    placeholder="Description..."
                    className="text-sm"
                  />
                  <Input
                    value={variation.usage}
                    onChange={(e) => updateVariation(variation.id, 'usage', e.target.value)}
                    placeholder="Usage guidelines..."
                    className="text-sm"
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Usage Guidelines */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Usage Guidelines
          </CardTitle>
          <CardDescription>
            Do's and don'ts for proper logo usage
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Do's */}
            <div className="space-y-3">
              <Label className="text-green-700 font-medium">✓ Do</Label>
              <div className="flex space-x-2">
                <Input
                  placeholder="Add a guideline to follow..."
                  value={newDo}
                  onChange={(e) => setNewDo(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addToList('dosList', newDo, setNewDo)}
                />
                <Button 
                  size="sm" 
                  onClick={() => addToList('dosList', newDo, setNewDo)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-2">
                {guidelines.dosList.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded text-sm">
                    <span className="text-green-800">{item}</span>
                    <button
                      onClick={() => removeFromList('dosList', index)}
                      className="text-green-600 hover:text-green-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Don'ts */}
            <div className="space-y-3">
              <Label className="text-red-700 font-medium">✗ Don't</Label>
              <div className="flex space-x-2">
                <Input
                  placeholder="Add something to avoid..."
                  value={newDont}
                  onChange={(e) => setNewDont(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addToList('dontsList', newDont, setNewDont)}
                />
                <Button 
                  size="sm" 
                  onClick={() => addToList('dontsList', newDont, setNewDont)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-2">
                {guidelines.dontsList.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded text-sm">
                    <span className="text-red-800">{item}</span>
                    <button
                      onClick={() => removeFromList('dontsList', index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage Examples */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Approved Usage Examples
          </CardTitle>
          <CardDescription>
            Select where your logo can be used appropriately
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {USAGE_SCENARIOS.map((scenario) => (
              <div 
                key={scenario.key}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  guidelines.usageExamples[scenario.key as keyof LogoGuidelines['usageExamples']]
                    ? 'border-green-300 bg-green-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => toggleUsageExample(scenario.key as keyof LogoGuidelines['usageExamples'])}
              >
                <div className="text-center">
                  <div className="text-2xl mb-2">{scenario.icon}</div>
                  <div className="font-medium text-sm">{scenario.label}</div>
                  {guidelines.usageExamples[scenario.key as keyof LogoGuidelines['usageExamples']] && (
                    <CheckCircle className="w-4 h-4 text-green-600 mx-auto mt-2" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Color Usage */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Color Usage Guidelines
          </CardTitle>
          <CardDescription>
            Specify approved color combinations and restrictions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Primary Colors</Label>
              <Textarea
                value={guidelines.colorUsage.primary.join(', ')}
                onChange={(e) => setGuidelines(prev => ({
                  ...prev,
                  colorUsage: { ...prev.colorUsage, primary: e.target.value.split(', ').filter(Boolean) }
                }))}
                placeholder="List primary logo colors..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>On Dark Backgrounds</Label>
              <Textarea
                value={guidelines.colorUsage.onDark.join(', ')}
                onChange={(e) => setGuidelines(prev => ({
                  ...prev,
                  colorUsage: { ...prev.colorUsage, onDark: e.target.value.split(', ').filter(Boolean) }
                }))}
                placeholder="Colors for dark backgrounds..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>On Light Backgrounds</Label>
              <Textarea
                value={guidelines.colorUsage.onLight.join(', ')}
                onChange={(e) => setGuidelines(prev => ({
                  ...prev,
                  colorUsage: { ...prev.colorUsage, onLight: e.target.value.split(', ').filter(Boolean) }
                }))}
                placeholder="Colors for light backgrounds..."
                rows={2}
              />
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              Color Restrictions
            </Label>
            <div className="flex flex-wrap gap-2">
              {guidelines.colorUsage.restrictions.map((restriction, index) => (
                <Badge key={index} variant="outline" className="flex items-center gap-1">
                  {restriction}
                  <button
                    onClick={() => removeColorRestriction(index)}
                    className="ml-1 hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex space-x-2">
              <Input
                placeholder="Add color restriction..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addColorRestriction((e.target as HTMLInputElement).value);
                    (e.target as HTMLInputElement).value = ''
                  }
                }}
              />
              <Button 
                size="sm" 
                variant="outline"
                onClick={(e) => {
                  const input = e.currentTarget.previousElementSibling as HTMLInputElement
                  addColorRestriction(input.value)
                  input.value = ''
                }}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}